import { ZyVersionBadge } from '@/components/tiptap-editor/tiptap-extension/zyversion-extension/zyversion-badge';
import type { SplitViewEditorEntry } from '@/hooks/use-split-view';
import { attachDeltaScrollSync, paneScrollElement } from '@/lib/split-view-scroll-sync';
import type { Editor } from '@tiptap/react';
import { EditorContent } from '@tiptap/react';
import { useEffect } from 'react';
import { TiptapEditorPlugins } from './tiptap-editor-plugins';

/** Layout for split zyVersion panes; pane creation and toggle live in `useSplitView`. */

interface SplitViewPaneProps {
    version: string;
    editor: Editor;
    scrollLocked?: boolean;
    onFocus?: (editor: Editor) => void;
}

export interface SplitViewPanesProps {
    editors: SplitViewEditorEntry[];
    syncScroll?: boolean;
    onFocus?: (editor: Editor) => void;
}

function SplitViewPaneInner({ version, editor, scrollLocked = false, onFocus }: SplitViewPaneProps) {
    return (
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded border border-gray-200 dark:border-gray-700">
            <div
                className="border-b border-gray-200 bg-gray-50 px-2 py-1 dark:border-gray-700 dark:bg-gray-800"
                contentEditable={false}
            >
                <ZyVersionBadge version={version} />
            </div>
            <div
                data-split-pane-scroll="true"
                data-split-scroll-locked={scrollLocked ? 'true' : undefined}
                className="min-w-0 h-full w-full overflow-auto"
                onFocus={() => onFocus?.(editor)}
                role="region"
            >
                {/* Editor Plugins */}
                {editor && <TiptapEditorPlugins editor={editor} />}

                {/* Editor Content */}
                <EditorContent
                    editor={editor}
                    role="presentation"
                    className="zybooks-editor-content"
                />
            </div>
        </div>
    );
}

export function SplitViewPanes({ editors, syncScroll = true, onFocus }: SplitViewPanesProps) {
    useEffect(() => {
        if (!syncScroll) {
            return;
        }

        const scrollEls = editors.map(({ editor }) => paneScrollElement(editor)).filter((el): el is HTMLElement => el != null);

        return attachDeltaScrollSync(scrollEls);
    }, [syncScroll, editors]);

    return (
        <div
            data-split-scroll-locked={syncScroll ? 'true' : undefined}
            className="min-h-0 flex-1 overflow-hidden mt-2 mx-4"
        >
            <div className="flex h-full gap-2">
                {editors.map(({ version, editor }) => (
                    <SplitViewPaneInner
                        key={`split-view-pane-${version}`}
                        version={version}
                        editor={editor}
                        scrollLocked={syncScroll}
                        onFocus={onFocus}
                    />
                ))}
            </div>
        </div>
    );
}
